// Copyright (c) Subzero Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { createRialoClient, type RialoClient, type RialoClientConfig } from "@rialo/ts-cdk";
import { Store } from "@tanstack/store";
import type { Wallet } from "@wallet-standard/base";
import { clearPendingConnections } from "./actions/connection";
import type { WalletEventBridge } from "./bridge/WalletEventBridge";
import type { WalletRegistry } from "./registry/WalletRegistry";
import {
  createPersistenceManager,
  loadPersistedState,
  type PersistenceManager,
} from "./storage/persistence";
import type { FrostAppState } from "./types";

export interface CreateConfigOptions {
  /**
   * Client configuration for the RPC client.
   *
   * Use one of the preset chains with `getDefaultRialoClientConfig()` or create
   * a custom config with your own chain definition and transport settings.
   *
   * @example
   * ```ts
   * import { getDefaultRialoClientConfig } from '@rialo/ts-cdk';
   *
   * // Using preset config
   * createConfig({ clientConfig: getDefaultRialoClientConfig('devnet') })
   *
   * // Using custom RPC URL
   * createConfig({
   *   clientConfig: {
   *     chain: {
   *       id: 'rialo:mainnet',
   *       name: 'Mainnet',
   *       rpcUrl: 'https://my-quicknode-endpoint.com',
   *     },
   *     transport: { timeout: 30000 }
   *   }
   * })
   * ```
   */
  clientConfig: RialoClientConfig;
  /** Auto-reconnect on page load (default: true) */
  autoConnect?: boolean;
  /** Session TTL in milliseconds (default: 7 days) */
  sessionTTL?: number;
  /** Storage key prefix (default: 'rialo-frost') */
  storageKey?: string;
  /** Custom storage implementation (default: localStorage) */
  storage?: Storage | null;
}

/** Required config options after defaults are applied */
export type ResolvedConfigOptions = Readonly<Required<CreateConfigOptions>>;

export interface FrostConfig {
  /** Core reactive store */
  readonly store: Store<FrostAppState>;

  /**
   * RPC client for blockchain queries and transactions.
   * Use `switchChain()` to switch to a different chain.
   */
  readonly client: RialoClient;

  /**
   * Get the current chain identifier from the client.
   * This is the single source of truth for chain ID.
   */
  getChainId(): string;

  /**
   * Switch to a different chain/network.
   * Creates a new RPC client and updates the store's chainId.
   *
   * @param clientConfig - New client configuration (chain, endpoint, transport)
   */
  switchChain(clientConfig: RialoClientConfig): void;

  /** Internal: Wallet discovery service */
  _registry: WalletRegistry | null;

  /** Internal: Wallet event handler */
  _bridge: WalletEventBridge | null;

  /** Internal: Persistence manager */
  _persistence: PersistenceManager | null;

  /** Resolved configuration options */
  readonly options: ResolvedConfigOptions;

  /** Cleanup all subscriptions and resources */
  destroy(): void;
}

const DEFAULT_OPTIONS: Omit<Required<CreateConfigOptions>, "clientConfig"> = {
  autoConnect: true,
  sessionTTL: 7 * 24 * 60 * 60 * 1000, // 7 days
  storageKey: "rialo-frost",
  storage: typeof window !== "undefined" ? localStorage : null,
};

/**
 * Creates a Frost configuration object.
 *
 * **Important:** Create the config outside of React components to ensure
 * it's only created once and remains stable across re-renders.
 *
 * @param options - Configuration options including client config and optional settings
 * @returns A Frost configuration object to be passed to `FrostProvider`
 *
 * @example
 * ```ts
 * import { createConfig, getDefaultRialoClientConfig } from '@rialo/frost';
 *
 * export const config = createConfig({
 *   clientConfig: getDefaultRialoClientConfig('devnet'),
 *   autoConnect: true,
 * });
 * ```
 */
export function createConfig(options: CreateConfigOptions): FrostConfig {
  const opts: ResolvedConfigOptions = { ...DEFAULT_OPTIONS, ...options };

  // Create initial RialoClient for blockchain queries and transactions
  let client = createRialoClient(opts.clientConfig);

  // Get chain ID from client config (single source of truth)
  const chainId = opts.clientConfig.chain.id;

  // Create persistence manager (encapsulates debounce per-config)
  const persistence = opts.storage ? createPersistenceManager(opts.storageKey, opts.storage) : null;

  // Load persisted state or create initial state
  const persisted = opts.storage
    ? loadPersistedState(opts.storageKey, opts.storage, chainId)
    : null;

  const initialState: FrostAppState = persisted
    ? { ...persisted, _walletRefs: new Map<string, Wallet>() }
    : {
        status: "disconnected",
        chainId,
        walletName: null,
        accountAddress: null,
        connectedAt: null,
        sessionExpiresAt: null,
        lastUsedAt: Date.now(),
        userDisconnected: false,
        wallets: new Map(),
        accounts: new Map(),
        _walletRefs: new Map<string, Wallet>(),
      };

  // Create main store with persistence
  const store = new Store<FrostAppState>(initialState, {
    onUpdate: (): void => {
      persistence?.persist(store.state);
    },
  });

  const config: FrostConfig = {
    store,
    get client(): RialoClient {
      return client;
    },
    getChainId(): string {
      return store.state.chainId;
    },
    switchChain(clientConfig: RialoClientConfig): void {
      // Create new client with the new config
      client = createRialoClient(clientConfig);

      // Update chainId in state (derived from client config)
      store.setState(state => ({
        ...state,
        chainId: clientConfig.chain.id,
      }));
    },
    _registry: null, // Will be initialized by initializeConfig
    _bridge: null, // Will be initialized by initializeConfig
    _persistence: persistence,
    options: opts,
    destroy(): void {
      // Clear any pending connection promises
      clearPendingConnections(config);

      // Cleanup internal services
      config._registry?.destroy();
      config._bridge?.destroy();
      config._persistence?.destroy();
    },
  };

  return config;
}

/**
 * Initializes wallet discovery and event handling for the config.
 *
 * Called internally by `FrostProvider`. You typically don't need to call this directly.
 *
 * @param config - The Frost configuration object
 * @param registry - Wallet registry for discovering available wallets
 * @param bridge - Event bridge for handling wallet events
 * @internal
 */
export function initializeConfig(
  config: FrostConfig,
  registry: WalletRegistry,
  bridge: WalletEventBridge
): void {
  config._registry = registry;
  config._bridge = bridge;
}
